// src/pages/lead/LeadAssignedCandidates.jsx
// ============================================================
// Team Lead Assigned Candidates Module
// Automatic sync between HR ATS Scheduling & Team Lead Panel.
// ============================================================

import React, { useState, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useCandidates, submitLeadRecommendation } from "../../hooks/useRecruitment";
import { toast } from "react-hot-toast";
import {
  Search,
  FileText,
  Download,
  Video,
  Calendar,
  UserCheck,
  Award,
  XCircle,
  Eye,
  Phone,
  Mail,
  Briefcase,
} from "lucide-react";

export default function LeadAssignedCandidates() {
  const { userProfile } = useAuth();
  const uid = userProfile?.uid || userProfile?.id;
  const leadName = userProfile?.displayName || userProfile?.name || "Team Lead";
  const userDept = userProfile?.department || "";

  const { data: candidates, loading } = useCandidates();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Candidate Detail View Modal
  const [profileModalCandidate, setProfileModalCandidate] = useState(null);

  // Recommendation & Rating Modal State
  const [showRecModal, setShowRecModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [recommendation, setRecommendation] = useState("Proceed");
  const [technicalRating, setTechnicalRating] = useState(8);
  const [leadComments, setLeadComments] = useState("");
  const [saving, setSaving] = useState(false);

  // Filter candidates automatically assigned to this Team Lead
  const assignedCandidates = useMemo(() => {
    return candidates.filter((c) => {
      // Role-based department-aware match or direct assignment by ID/name
      const isAssigned =
        c.assignedLeadId === uid ||
        (c.assignedLeadName && c.assignedLeadName.toLowerCase().includes(leadName.toLowerCase())) ||
        (c.interviewerName && c.interviewerName.toLowerCase().includes(leadName.toLowerCase())) ||
        (c.stage === "Assigned to Team Lead" && userDept && (c.department || "").toLowerCase() === userDept.toLowerCase()) ||
        (c.stage === "Interview Scheduled" && userDept && (c.department || "").toLowerCase() === userDept.toLowerCase()) ||
        (!c.assignedLeadId && userDept && (c.department || "").toLowerCase() === userDept.toLowerCase());

      if (!isAssigned) return false;

      const matchSearch =
        (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.email || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.appliedPosition || c.role || "").toLowerCase().includes(search.toLowerCase());

      const matchStatus =
        statusFilter === "all" ||
        (statusFilter === "pending" && (!c.leadRecommendation || c.leadRecommendation === "Pending Review")) ||
        c.leadRecommendation === statusFilter ||
        (statusFilter === "Proceed" && (c.leadRecommendation === "Proceed" || c.leadRecommendation === "Proceed to Technical Interview"));

      return matchSearch && matchStatus;
    });
  }, [candidates, uid, leadName, userDept, search, statusFilter]);

  function handleOpenEvalModal(c) {
    setSelectedCandidate(c);
    setRecommendation(
      c.leadRecommendation && c.leadRecommendation !== "Pending Review"
        ? (c.leadRecommendation.includes("Proceed") ? "Proceed" : c.leadRecommendation)
        : "Proceed"
    );
    setTechnicalRating(c.technicalRating || c.rating || 8);
    setLeadComments(c.leadComments || "");
    setShowRecModal(true);
  }

  async function handleRecSubmit(e) {
    e.preventDefault();
    if (!selectedCandidate) return;
    setSaving(true);

    try {
      await submitLeadRecommendation(
        selectedCandidate.id,
        recommendation,
        leadComments,
        leadName,
        technicalRating
      );

      toast.success(`Technical evaluation submitted for ${selectedCandidate.name}! Automatically assigned to Admin for final decision.`);
      setShowRecModal(false);
    } catch (err) {
      toast.error("Failed to submit recommendation: " + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .lead-cand-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 1.25rem;
        }
        .lead-cand-card {
          background: var(--bg-card, #ffffff);
          border: 1px solid var(--border, #e2e8f0);
          border-radius: 16px;
          padding: 1.35rem;
          box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
          display: flex;
          flex-direction: column;
          gap: 1rem;
          transition: all 0.2s ease-in-out;
        }
        .lead-cand-card:hover {
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
          border-color: #cbd5e1;
        }
        .hr-notes-box {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.75rem 0.9rem;
          font-size: 0.8rem;
          color: #334155;
        }
        .int-details-box {
          background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
          border: 1.5px solid #a7f3d0;
          border-radius: 12px;
          padding: 0.85rem 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .btn-join-meet {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          background: linear-gradient(135deg, #1b4b31 0%, #065f46 100%);
          color: #ffffff;
          padding: 0.45rem 0.95rem;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.78rem;
          text-decoration: none;
          box-shadow: 0 2px 6px rgba(27, 75, 49, 0.2);
          transition: all 0.2s ease;
        }
        .btn-join-meet:hover {
          background: linear-gradient(135deg, #143724 0%, #044e39 100%);
          color: #ffffff;
          box-shadow: 0 4px 10px rgba(27, 75, 49, 0.3);
        }
        .resume-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.35rem 0.7rem;
          font-size: 0.75rem;
          font-weight: 700;
          border-radius: 7px;
          border: 1px solid #cbd5e1;
          background: #ffffff;
          color: #0f172a;
          text-decoration: none;
          transition: all 0.15s ease;
        }
        .resume-action-btn:hover {
          background: #f1f5f9;
          border-color: #94a3b8;
        }

        /* ── Enterprise Search Toolbar ───────────────────────── */
        .lac-toolbar {
          display: flex;
          align-items: center;
          gap: 0.85rem;
          flex-wrap: wrap;
        }
        .lac-search-wrap {
          flex: 1;
          min-width: 280px;
          position: relative;
          display: flex;
          align-items: center;
        }
        .lac-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #64748b;
          pointer-events: none;
          transition: color 0.2s ease;
        }
        .lac-search-wrap:focus-within .lac-search-icon {
          color: #1b4b31;
        }
        .lac-search-input {
          width: 100%;
          height: 46px;
          padding: 0 3rem 0 2.75rem;
          border: 1.5px solid var(--border, #e2e8f0);
          border-radius: 11px;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-primary, #0f172a);
          background: var(--bg-card, #ffffff);
          outline: none;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
          transition: all 0.2s ease;
          box-sizing: border-box;
        }
        .lac-search-input::placeholder {
          color: #94a3b8;
          font-weight: 400;
        }
        .lac-search-input:focus {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12), 0 2px 8px rgba(27, 75, 49, 0.06);
          background: #ffffff;
        }
        .lac-search-clear {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          border: none;
          background: #e2e8f0;
          color: #475569;
          border-radius: 50%;
          cursor: pointer;
          padding: 0;
          transition: all 0.15s ease;
        }
        .lac-search-clear:hover {
          background: #cbd5e1;
          color: #0f172a;
        }
        .lac-filter-select {
          height: 46px;
          padding: 0 1rem;
          border: 1.5px solid var(--border, #e2e8f0);
          border-radius: 11px;
          font-size: 0.875rem;
          font-weight: 500;
          background: var(--bg-card, #ffffff);
          color: var(--text-primary, #1e293b);
          outline: none;
          cursor: pointer;
          min-width: 190px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
          transition: all 0.2s ease;
          appearance: auto;
        }
        .lac-filter-select:hover { border-color: #cbd5e1; }
        .lac-filter-select:focus {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12);
        }
        @media (max-width: 640px) {
          .lac-toolbar { flex-direction: column; align-items: stretch; }
          .lac-search-wrap { min-width: 0; }
          .lac-filter-select { width: 100%; }
        }

        /* ── Technical Evaluation Modal ──────────────────────── */
        .eval-modal-body {
          background: #ffffff;
          border-radius: 18px;
          width: 100%;
          max-width: 760px;
          max-height: 92vh;
          overflow-y: auto;
          box-shadow: 0 24px 48px -12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0,0,0,0.06);
          box-sizing: border-box;
          position: relative;
          animation: atsSlideUp 0.28s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .eval-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1.5rem 2rem 1.25rem;
          border-bottom: 1px solid #f1f5f9;
          background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 60%);
          border-radius: 18px 18px 0 0;
        }
        .eval-modal-content {
          padding: 1.75rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.4rem;
        }
        .eval-candidate-badge {
          display: flex;
          align-items: center;
          gap: 0.85rem;
          background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
          border: 1px solid #a7f3d0;
          border-radius: 12px;
          padding: 0.9rem 1.1rem;
        }
        .eval-candidate-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1b4b31, #065f46);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          font-weight: 800;
          color: #ffffff;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(27, 75, 49, 0.3);
        }
        .eval-two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.1rem;
        }
        @media (max-width: 560px) {
          .eval-two-col { grid-template-columns: 1fr; }
          .eval-modal-header { padding: 1.25rem 1.25rem 1rem; }
          .eval-modal-content { padding: 1.25rem; }
        }
        .eval-form-field {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }
        .eval-form-label {
          font-size: 0.8rem;
          font-weight: 700;
          color: #334155;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          letter-spacing: 0.01em;
        }
        .eval-form-label .req { color: #ef4444; }
        .eval-select, .eval-input {
          width: 100%;
          box-sizing: border-box;
          height: 46px;
          padding: 0 0.95rem;
          border: 1.5px solid #cbd5e1;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          background: #ffffff;
          color: #0f172a;
          outline: none;
          transition: all 0.2s ease;
        }
        .eval-select:focus, .eval-input:focus {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12);
        }
        .eval-textarea {
          width: 100%;
          box-sizing: border-box;
          min-height: 150px;
          padding: 0.8rem 0.95rem;
          border: 1.5px solid #cbd5e1;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 400;
          background: #ffffff;
          color: #0f172a;
          outline: none;
          resize: vertical;
          font-family: inherit;
          line-height: 1.6;
          transition: all 0.2s ease;
        }
        .eval-textarea:focus {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12);
        }
        .eval-textarea::placeholder { color: #94a3b8; }
        .eval-helper {
          font-size: 0.74rem;
          color: #64748b;
          margin-top: 2px;
        }
        .eval-rating-dots {
          display: flex;
          gap: 5px;
          margin-top: 4px;
          flex-wrap: wrap;
        }
        .eval-rating-dot {
          width: 24px;
          height: 8px;
          border-radius: 4px;
          transition: all 0.15s ease;
        }
        .eval-modal-footer {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 0.75rem;
          padding: 1.1rem 2rem 1.5rem;
          border-top: 1px solid #f1f5f9;
        }
        .eval-btn-cancel {
          height: 44px;
          padding: 0 1.35rem;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 600;
          background: #ffffff;
          color: #334155;
          border: 1.5px solid #e2e8f0;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .eval-btn-cancel:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #0f172a;
        }
        .eval-btn-submit {
          height: 44px;
          padding: 0 1.5rem;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 700;
          background: linear-gradient(135deg, #1b4b31 0%, #065f46 100%);
          color: #ffffff;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 2px 8px rgba(27, 75, 49, 0.25);
          transition: all 0.2s ease;
          min-width: 220px;
          justify-content: center;
        }
        .eval-btn-submit:hover:not(:disabled) {
          background: linear-gradient(135deg, #143824 0%, #044e39 100%);
          box-shadow: 0 4px 14px rgba(27, 75, 49, 0.35);
          transform: translateY(-1px);
        }
        .eval-btn-submit:disabled {
          opacity: 0.65;
          cursor: not-allowed;
          transform: none;
        }
        .eval-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: evalSpin 0.7s linear infinite;
        }
        @keyframes evalSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header Banner */}
      <div style={{ background: "linear-gradient(135deg, #1b4b31, #065f46)", color: "#fff", padding: "1.35rem 1.6rem", borderRadius: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.85rem", boxShadow: "0 4px 16px rgba(27, 75, 49, 0.2)" }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 800, fontSize: "1.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <UserCheck size={24} /> Assigned Candidates (Technical Evaluation)
          </h2>
          <p style={{ margin: "4px 0 0 0", fontSize: "0.82rem", opacity: 0.9 }}>
            Candidates scheduled for technical interviews with Team Lead <b>{leadName}</b>. Review resumes, join meetings, and submit ratings & recommendations.
          </p>
        </div>
        <div style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(4px)", padding: "0.45rem 1rem", borderRadius: 99, fontSize: "0.8rem", fontWeight: 800, border: "1px solid rgba(255,255,255,0.25)" }}>
          {assignedCandidates.length} Assigned Candidates
        </div>
      </div>

      {/* ── Enterprise Search & Filter Toolbar ───────────────── */}
      <div className="lac-toolbar">
        {/* Full-Width Search Bar */}
        <div className="lac-search-wrap">
          <Search size={18} className="lac-search-icon" />
          <input
            id="lac-candidate-search"
            type="text"
            className="lac-search-input"
            placeholder="Search by Candidate Name, Email, Position, or Department..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
          {search && (
            <button
              type="button"
              className="lac-search-clear"
              onClick={() => setSearch("")}
              title="Clear search"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* Recommendation Filter */}
        <select
          id="lac-recommendation-filter"
          className="lac-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Recommendations</option>
          <option value="pending">Awaiting Evaluation</option>
          <option value="Proceed">Proceed to Next Round</option>
          <option value="Hold">Hold</option>
          <option value="Reject">Reject</option>
        </select>
      </div>

      {/* Assigned Candidate Cards */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "3.5rem", color: "var(--text-muted)", background: "#fff", borderRadius: 16 }}>Loading assigned candidates...</div>
      ) : assignedCandidates.length === 0 ? (
        <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 16, padding: "4rem 1.5rem", textAlign: "center", color: "var(--text-muted)" }}>
          <FileText size={48} style={{ color: "#cbd5e1", marginBottom: 10 }} />
          <h3 style={{ margin: "0 0 6px", fontSize: "1.1rem", color: "#0f172a", fontWeight: 700 }}>No Candidates Currently Assigned</h3>
          <p style={{ margin: 0, fontSize: "0.85rem" }}>
            When HR schedules an interview and assigns a candidate to you, their profile, resume, and meeting link will automatically appear here.
          </p>
        </div>
      ) : (
        <div className="lead-cand-grid">
          {assignedCandidates.map((c) => {
            const hasRec = c.leadRecommendation && c.leadRecommendation !== "Pending Review";
            const isProceed = c.leadRecommendation === "Proceed" || c.leadRecommendation === "Proceed to Technical Interview";
            const meetingLink = c.meetingLink || "https://meet.google.com/xyz-abc-123";

            return (
              <div key={c.id} className="lead-cand-card">
                {/* Header Row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#0f172a" }}>{c.name}</h3>
                    <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#1b4b31", marginTop: 2, display: "flex", alignItems: "center", gap: "0.35rem" }}>
                      <Briefcase size={14} /> {c.appliedPosition || c.role || "Applicant"}
                    </div>
                  </div>
                  <span style={{ fontSize: "0.73rem", fontWeight: 700, padding: "0.2rem 0.6rem", borderRadius: 99, background: "#e0e7ff", color: "#3730a3" }}>
                    {c.stage || "Assigned to Lead"}
                  </span>
                </div>

                {/* Candidate Contact & Info */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.45rem", fontSize: "0.78rem", background: "#f8fafc", padding: "0.65rem 0.8rem", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", color: "#475569" }}>
                    <Mail size={13} style={{ color: "#1b4b31" }} /> <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", color: "#475569" }}>
                    <Phone size={13} style={{ color: "#1b4b31" }} /> <span>{c.phone || c.phoneNumber || "—"}</span>
                  </div>
                  <div style={{ color: "#475569" }}>Experience: <b>{c.experience || "3 years"}</b></div>
                  <div style={{ color: "#475569" }}>Dept: <b>{c.department || userDept || "Engineering"}</b></div>
                </div>

                {/* HR Screening Notes (Read-Only) */}
                <div className="hr-notes-box">
                  <div style={{ fontWeight: 700, fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase", marginBottom: 3, display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    🔒 HR Screening Notes (Read-Only)
                  </div>
                  <div style={{ fontSize: "0.78rem", color: c.hrScreeningNotes ? "#0f172a" : "#64748b", fontStyle: c.hrScreeningNotes ? "normal" : "italic" }}>
                    "{c.hrScreeningNotes || c.notes || "Shortlisted and assigned by HR for technical skills evaluation."}"
                  </div>
                </div>

                {/* Interview Details & Direct Join Meeting Box */}
                <div className="int-details-box">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontWeight: 800, fontSize: "0.84rem", color: "#065f46", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                      <Calendar size={15} /> {c.scheduledRound || c.round || "Technical Round 1"}
                    </div>
                    <span style={{ fontSize: "0.7rem", fontWeight: 700, background: "#d1fae5", color: "#065f46", padding: "0.15rem 0.5rem", borderRadius: 99 }}>
                      {c.meetingMode || "Google Meet"}
                    </span>
                  </div>

                  <div style={{ fontSize: "0.76rem", color: "#166534", display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <span>📅 <b>{c.scheduledDate || "Scheduled Date"}</b></span>
                    <span>⏰ <b>{c.scheduledTime || "14:00"}</b> ({c.scheduledDuration || "45m"})</span>
                  </div>

                  {c.specialInstructions && (
                    <div style={{ fontSize: "0.74rem", color: "#15803d" }}>
                      📝 Topics: {c.specialInstructions}
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                    <a
                      href={meetingLink}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-join-meet"
                    >
                      <Video size={14} /> Join Meeting
                    </a>
                    <span style={{ fontSize: "0.72rem", color: "#64748b" }}>
                      Link: {meetingLink.slice(0, 24)}...
                    </span>
                  </div>
                </div>

                {/* Resume View & Download Actions */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "0.3rem" }}>
                  <div style={{ display: "flex", gap: "0.4rem" }}>
                    {c.resumeUrl ? (
                      <>
                        <a
                          href={c.resumeUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="resume-action-btn"
                          title="View Resume PDF"
                        >
                          <Eye size={13} style={{ color: "#0284c7" }} /> View Resume
                        </a>
                        <a
                          href={c.resumeUrl}
                          download={c.resumeFileName || `${c.name}_Resume.pdf`}
                          target="_blank"
                          rel="noreferrer"
                          className="resume-action-btn"
                          title="Download Resume"
                        >
                          <Download size={13} style={{ color: "#16a34a" }} /> Download
                        </a>
                      </>
                    ) : (
                      <span style={{ fontSize: "0.74rem", color: "#64748b", fontStyle: "italic" }}>
                        📄 Resume attached to profile
                      </span>
                    )}
                  </div>

                  <button
                    className="resume-action-btn"
                    onClick={() => setProfileModalCandidate(c)}
                    title="Full Profile"
                  >
                    <FileText size={13} style={{ color: "#6366f1" }} /> Full Profile
                  </button>
                </div>

                {/* Recommendation & Evaluation Actions */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "0.75rem", borderTop: "1px solid #f1f5f9" }}>
                  <div>
                    {hasRec ? (
                      <div>
                        <span style={{
                          padding: "0.22rem 0.65rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 800,
                          background: isProceed ? "#d1fae5" : c.leadRecommendation === "Reject" ? "#fee2e2" : "#fef3c7",
                          color: isProceed ? "#065f46" : c.leadRecommendation === "Reject" ? "#991b1b" : "#92400e"
                        }}>
                          {isProceed ? "✔ Proceed" : c.leadRecommendation}
                        </span>
                        {c.technicalRating && (
                          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#1b4b31", marginTop: 2 }}>
                            ⭐ Rating: {c.technicalRating}/10
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ padding: "0.2rem 0.6rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700, background: "#fef3c7", color: "#92400e" }}>
                        ⏳ Pending Evaluation
                      </span>
                    )}
                  </div>

                  <button
                    className="btn-primary"
                    style={{ padding: "0.4rem 0.85rem", fontSize: "0.78rem" }}
                    onClick={() => handleOpenEvalModal(c)}
                  >
                    {hasRec ? "✏️ Update Evaluation" : "📝 Technical Review"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Candidate Full Profile Modal */}
      {profileModalCandidate && (
        <div className="modal-overlay" onClick={() => setProfileModalCandidate(null)}>
          <div className="modal-body" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-title-icon">
                  <UserCheck size={22} />
                </div>
                <div>
                  <h3 className="modal-title">{profileModalCandidate.name}</h3>
                  <p className="modal-subtitle">Candidate Technical Evaluation Profile</p>
                </div>
              </div>
              <button type="button" className="modal-close-btn" onClick={() => setProfileModalCandidate(null)}><XCircle size={18} /></button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", fontSize: "0.85rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", background: "#f8fafc", padding: "1rem", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                <div>Position: <b>{profileModalCandidate.appliedPosition || "Applicant"}</b></div>
                <div>Department: <b>{profileModalCandidate.department || "Engineering"}</b></div>
                <div>Email: <b>{profileModalCandidate.email}</b></div>
                <div>Phone: <b>{profileModalCandidate.phone || "—"}</b></div>
                <div>Experience: <b>{profileModalCandidate.experience || "3 years"}</b></div>
                <div>Stage: <b>{profileModalCandidate.stage || "Assigned"}</b></div>
              </div>

              <div>
                <h4 style={{ margin: "0 0 4px", fontSize: "0.85rem", fontWeight: 700, color: "#334155" }}>HR Screening Notes</h4>
                <div className="hr-notes-box">"{profileModalCandidate.hrScreeningNotes || "Shortlisted by HR."}"</div>
              </div>

              <div>
                <h4 style={{ margin: "0 0 4px", fontSize: "0.85rem", fontWeight: 700, color: "#334155" }}>Interview Details</h4>
                <div className="int-details-box">
                  <div>Round: <b>{profileModalCandidate.scheduledRound || "Technical Round 1"}</b></div>
                  <div>Date & Time: <b>{profileModalCandidate.scheduledDate} at {profileModalCandidate.scheduledTime}</b> ({profileModalCandidate.scheduledDuration || "45m"})</div>
                  <div>Mode: <b>{profileModalCandidate.meetingMode || "Google Meet"}</b></div>
                  {profileModalCandidate.meetingLink && (
                    <div>Link: <a href={profileModalCandidate.meetingLink} target="_blank" rel="noreferrer" style={{ color: "#0284c7", fontWeight: 700 }}>{profileModalCandidate.meetingLink}</a></div>
                  )}
                </div>
              </div>

              {profileModalCandidate.resumeUrl && (
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <a href={profileModalCandidate.resumeUrl} target="_blank" rel="noreferrer" className="btn-primary" style={{ padding: "0.45rem 1rem", fontSize: "0.8rem" }}>
                    <Eye size={15} /> View Resume PDF
                  </a>
                  <a href={profileModalCandidate.resumeUrl} download target="_blank" rel="noreferrer" className="btn-secondary" style={{ padding: "0.45rem 1rem", fontSize: "0.8rem" }}>
                    <Download size={15} /> Download Resume
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Technical Evaluation Modal (Enterprise Redesign) ── */}
      {showRecModal && selectedCandidate && (
        <div className="modal-overlay" onClick={() => !saving && setShowRecModal(false)}>
          <div className="eval-modal-body" onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="eval-modal-header">
              <div style={{ display: "flex", alignItems: "center", gap: "0.9rem" }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: "linear-gradient(135deg, #1b4b31, #065f46)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(27,75,49,0.25)", flexShrink: 0
                }}>
                  <Award size={24} color="#ffffff" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                    Submit Technical Evaluation
                  </h3>
                  <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "#64748b" }}>
                    Evaluating candidate for <b style={{ color: "#1b4b31" }}>{selectedCandidate.appliedPosition || selectedCandidate.role || "the applied position"}</b>
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => !saving && setShowRecModal(false)}
                title="Close"
                style={{ marginTop: 2, flexShrink: 0 }}
              >
                <XCircle size={18} />
              </button>
            </div>

            <div className="eval-modal-content">
              {/* Candidate Identity Badge */}
              <div className="eval-candidate-badge">
                <div className="eval-candidate-avatar">
                  {(selectedCandidate.name || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "1rem", color: "#0f172a" }}>{selectedCandidate.name}</div>
                  <div style={{ fontSize: "0.78rem", color: "#475569", marginTop: 2 }}>
                    {selectedCandidate.email}
                    {selectedCandidate.department && <span style={{ marginLeft: 8, background: "#d1fae5", color: "#065f46", padding: "1px 8px", borderRadius: 99, fontWeight: 700, fontSize: "0.72rem" }}>{selectedCandidate.department}</span>}
                  </div>
                </div>
              </div>

              <form id="eval-form" onSubmit={handleRecSubmit}>
                {/* Two-Column Row: Recommendation + Rating */}
                <div className="eval-two-col">
                  {/* Recommendation Decision */}
                  <div className="eval-form-field">
                    <label htmlFor="eval-recommendation" className="eval-form-label">
                      Recommendation Decision <span className="req">*</span>
                    </label>
                    <select
                      id="eval-recommendation"
                      className="eval-select"
                      required
                      value={recommendation}
                      onChange={(e) => setRecommendation(e.target.value)}
                    >
                      <option value="Proceed">✔ Proceed to Next Round / Hire</option>
                      <option value="Hold">⏳ Hold / Secondary Evaluation</option>
                      <option value="Reject">❌ Reject Candidate</option>
                    </select>
                    <div className="eval-helper">
                      {recommendation === "Proceed" && "✅ Candidate will move to the next hiring stage."}
                      {recommendation === "Hold" && "⚠️ Candidate will be placed on hold for further review."}
                      {recommendation === "Reject" && "❌ Candidate will be marked as not suitable."}
                    </div>
                  </div>

                  {/* Technical Rating */}
                  <div className="eval-form-field">
                    <label htmlFor="eval-rating" className="eval-form-label">
                      Technical Rating <span className="req">*</span>
                    </label>
                    <input
                      id="eval-rating"
                      type="number"
                      min="1"
                      max="10"
                      step="0.5"
                      className="eval-input"
                      required
                      value={technicalRating}
                      onChange={(e) => setTechnicalRating(Math.min(10, Math.max(1, Number(e.target.value))))}
                      placeholder="1 – 10"
                    />
                    {/* Visual rating bar */}
                    <div className="eval-rating-dots">
                      {Array.from({ length: 10 }, (_, i) => (
                        <div
                          key={i}
                          className="eval-rating-dot"
                          style={{
                            background: i < technicalRating
                              ? (technicalRating >= 8 ? "#10b981" : technicalRating >= 5 ? "#f59e0b" : "#ef4444")
                              : "#e2e8f0"
                          }}
                        />
                      ))}
                    </div>
                    <div className="eval-helper">
                      {technicalRating >= 8 ? "🟢 Excellent" : technicalRating >= 5 ? "🟡 Average" : "🔴 Below Average"} · Score: {technicalRating}/10
                    </div>
                  </div>
                </div>

                {/* Feedback Textarea - Full Width */}
                <div className="eval-form-field" style={{ marginTop: "0.25rem" }}>
                  <label htmlFor="eval-feedback" className="eval-form-label">
                    Technical Assessment Feedback & Notes
                  </label>
                  <textarea
                    id="eval-feedback"
                    className="eval-textarea"
                    placeholder="Provide detailed feedback covering: coding proficiency, domain knowledge, problem-solving skills, communication, strengths, areas of improvement, and any other relevant observations from the technical interview..."
                    value={leadComments}
                    onChange={(e) => setLeadComments(e.target.value)}
                  />
                  <div className="eval-helper">
                    Your feedback is confidential and will be reviewed only by HR and senior management.
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="eval-modal-footer">
              <button
                type="button"
                className="eval-btn-cancel"
                onClick={() => !saving && setShowRecModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="eval-form"
                className="eval-btn-submit"
                disabled={saving}
              >
                {saving ? (
                  <><div className="eval-spinner" /> Submitting Evaluation...</>
                ) : (
                  <><Award size={16} /> Submit Technical Evaluation</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
